import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import FadeIn from '../../../components/animations/FadeIn';

const Services = () => {
  const [iconSlide, setIconSlide] = useState(0);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // Single hero background image (no slider)
  const heroImage = '/assets/images/bannerservice1.jpg';

  // Sticky icons
  const stickyIcons = [
    { id: 1, icon: '/assets/images/Solution4.png', title: 'DEVICE MANUFACTURING' },
    { id: 2, icon: '/assets/images/servicepageicon2.png', title: 'SUPPORT & CONSULTING' },
    { id: 3, icon: '/assets/images/servicepageicon3.png', title: 'APPLICATION DEVELOPMENT' },
  ];

  // ✅ 3 sections (update image paths + slugs as you want)
  // "slug" will navigate to: /services/:slug  -> ServiceDetail page
  const sections = [
    {
      id: 1,
      label: 'Device Manufacturing',
      heading: "Hardware Solutions",
      description:
        "SiWi's hardware engineering capabilities span the entire product lifecycle from concept design to mass manufacturing. Our in-house R&D team develops specialized GPS tracking devices that withstand the harshest operational environments, including extreme temperatures, constant vibration, water immersion, and dust exposure. The E-Rutu vehicle tracker exemplifies our design philosophy—integrating 4G/2G cellular, BLE 5.1 mesh, temperature sensors, and door monitoring in a compact IP67-rated enclosure that survives automotive underhood conditions.....",
      image: '/assets/images/service1.jpg',
      slug: 'device-manufacturing',
    },
    {
      id: 2,
      label: ' SOFTWARE DEVELOPMENT',
      heading: 'Software Solutions',
      description:
        'SiWi develops native mobile applications for iOS and Android that extend platform capabilities to field personnel and mobile managers. The driver application provides turn-by-turn navigation optimized for commercial vehicles, electronic logging for hours-of-service compliance, and digital proof-of-delivery capture including signatures, photos, and barcode scans. Real-time synchronization ensures dispatchers receive status updates instantly, while offline functionality maintains productivity in cellular dead zones with automatic queue-and-forward when connectivity restores.The manager application delivers dashboard analytics, alert acknowledgment, and remote configuration capabilities to smartphones and tablets.....',
      image: '/assets/images/service2.jpg',
      slug: 'mobile-application',
    },
    {
      id: 3,
      label: 'SUPPORT & CONSULTING',
      heading: 'Expert Support',
      description:
        "SiWi's customer success organization delivers comprehensive support spanning technical assistance, strategic consulting, and training programs. Our technical support center operates 24/7 with multilingual engineers available via phone, email, live chat, and remote diagnostics tools that accelerate issue resolution. Enterprise clients receive dedicated account managers who understand their operational context and provide proactive guidance on optimization opportunities.Implementation consulting ensures successful deployment through site surveys, network planning, installation supervision, and configuration validation. For complex integrations, our solutions architects collaborate.....",
      image: '/assets/images/service3.jpg',
      slug: 'support-consulting',
    },
  ];

  // Mobile icon slider handlers
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0 && iconSlide < stickyIcons.length - 1) setIconSlide((p) => p + 1);
      if (diff < 0 && iconSlide > 0) setIconSlide((p) => p - 1);
    }
  };

  const nextIconSlide = () => setIconSlide((p) => (p + 1) % stickyIcons.length);
  const prevIconSlide = () => setIconSlide((p) => (p - 1 + stickyIcons.length) % stickyIcons.length);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative w-full h-[360px] sm:h-[400px] md:h-[430px] lg:h-[560px] xl:h-[550px] overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <img src={heroImage} alt="Service background" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/20" />
        </div>

        {/* Content */}
        <div className="relative z-10 h-full flex flex-col items-center">
          {/* Desktop Title */}
          <div className="absolute top-[30px] sm:top-[45px] md:top-[55px] lg:top-[120px] left-0 right-0 px-4 hidden md:block">
            <FadeIn>
              <h1 className="text-base sm:text-lg md:text-2xl lg:text-3xl xl:text-4xl font-tight text-black-900 uppercase tracking-wide text-center">
                COMPREHENSIVE IOT SERVICES
              </h1>
            </FadeIn>
          </div>

          {/* Icons */}
          <div className="absolute bottom-[16px] sm:bottom-[22px] md:bottom-[28px] left-0 right-0 w-full">
            {/* Desktop Icons Row */}
            <div className="hidden md:block max-w-6xl mx-auto px-4">
              <div className="flex justify-center items-start gap-8 lg:gap-36">
                {stickyIcons.map((item) => (
                  <div key={item.id} className="flex flex-col items-center text-center w-[280px]">
                    <div className="w-60 h-60 lg:w-74 lg:h-74 xl:w-70 xl:h-70">
                      <img
                        src={item.icon}
                        alt={item.title}
                        className="w-full h-full object-contain drop-shadow-lg"
                      />
                    </div>
                    <h3 className="text-base lg:text-xl font-tight text-white-900 mb-1 leading-tight">
                      {item.title}
                    </h3>
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile Icons Slider */}
            <div className="md:hidden px-4">
              <div
                className="relative overflow-hidden"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <div
                  className="flex transition-transform duration-300 ease-out"
                  style={{ transform: `translateX(-${iconSlide * 100}%)` }}
                >
                  {stickyIcons.map((item) => (
                    <div
                      key={item.id}
                      className="w-full flex-shrink-0 flex flex-col items-center text-center px-8"
                    >
                      <div className="w-40 h-40 sm:w-44 sm:h-44 mb-2">
                        <img
                          src={item.icon}
                          alt={item.title}
                          className="w-full h-full object-contain drop-shadow-lg"
                        />
                      </div>
                      <h3 className="text-lg font-bold text-white-900 mb-1">{item.title}</h3>
                    </div>
                  ))}
                </div>

                {/* Mobile Nav */}
                <div className="flex justify-between absolute top-1/2 -translate-y-1/2 left-0 right-0 px-2">
                  <button
                    onClick={prevIconSlide}
                    className="p-2 bg-white/70 rounded-full shadow-md"
                    disabled={iconSlide === 0}
                  >
                    <ChevronLeft
                      className={`w-6 h-6 ${iconSlide === 0 ? 'text-gray-400' : 'text-gray-700'}`}
                    />
                  </button>
                  <button
                    onClick={nextIconSlide}
                    className="p-2 bg-white/70 rounded-full shadow-md"
                    disabled={iconSlide === stickyIcons.length - 1}
                  >
                    <ChevronRight
                      className={`w-6 h-6 ${
                        iconSlide === stickyIcons.length - 1 ? 'text-gray-400' : 'text-gray-700'
                      }`}
                    />
                  </button>
                </div>

                {/* Dots */}
                <div className="flex justify-center gap-2 mt-4">
                  {stickyIcons.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setIconSlide(idx)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        idx === iconSlide ? 'bg-gray-900 w-6' : 'bg-gray-400'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Title */}
          <div className="absolute bottom-[250px] sm:bottom-[260px] left-0 right-0 px-4 md:hidden">
            <FadeIn>
              <h1 className="text-base font-tight text-white-900 uppercase tracking-wide text-center">
                COMPREHENSIVE IOT SERVICES
              </h1>
            </FadeIn>
          </div>
        </div>
      </div>

      {/* ✅ Header like your style */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-[50px] pb-2 text-center">
        <FadeIn>
          <h2 className="text-2xl md:text-3xl lg:text-5xl font-medium tracking-tight text-gray-900/80">
            Discover how our expertise drives operational efficiency, reduces costs, and mitigates risks across industries worldwide....
          </h2>
        </FadeIn>
      </div>

      {/* ✅ Content Sections (3 total, alternating) */}
      <div className="bg-white">
        {sections.map((sec, idx) => {
          const isReverse = idx % 2 === 1; // alternate

          return (
            <div
              key={sec.id}
              className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 md:py-4"
            >
              <div
                className={`grid grid-cols-1 lg:grid-cols-2 items-center gap-10 lg:gap-20 ${
                  isReverse ? 'lg:[&>div:first-child]:order-2' : ''
                }`}
              >
                {/* Left: Text */}
                <FadeIn>
                  <div>
                    <p className="text-xs sm:text-sm tracking-widest font-semibold text-blue-700 uppercase mb-3">
                      {sec.label}
                    </p>

                    <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-[42px] leading-tight font-light text-gray-900 mb-5">
                      {sec.heading}
                    </h2>

                    <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed max-w-xl">
                      {sec.description}
                    </p>

                    {/* See more -> ServiceDetail (✅ opens detail page at TOP) */}
                    <div className="mt-6">
                      <Link
                        to={`/services/${sec.slug}`}
                        onClick={() => window.scrollTo(0, 0)}
                        className="inline-flex items-center gap-2 text-primary-600 font-semibold hover:underline"
                      >
                        See More <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </FadeIn>

                {/* Right: Image (NO card, slightly smaller) */}
                <FadeIn>
                  <img
                    src={sec.image}
                    alt={sec.heading}
                    className="w-full h-[500px] object-contain"
                  />
                </FadeIn>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Services;
